/* eslint-disable react/jsx-props-no-spreading */
/**
 * © 2024 Little Shilling, Inc.
 * Shon Little
 * Created: 2024-08-07
 */

// Add third-party dependencies.
import { useEffect, useState, useContext } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import {
  Box,
  Typography,
  Unstable_Grid2 as Grid,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Autocomplete,
} from '@mui/material';

// Add local dependencies.
import {
  getTodaysDate,
  roundDownTimeToHalfHour,
  addHours,
  calculateHours,
  isLongDuration,
} from '../../../assets/helpers/timecalc';
import AuthMenuGroup from '../AuthMenuGroup';
import { useAddActivityMutation } from '../../../api/employSlice';
import { useGetUserActivitiesQuery, useGetUserPayRatesQuery } from '../../../api/payrollSlice';
import { useGetUsersQuery } from '../../../api/authSlice';
import PageSpinner from '../../Common/PageSpinner';
import ErrorAlert from '../../Common/ErrorAlert';
import FormText from '../../Common/FormText';
import Question from '../../Common/Question';
import AuthContext from '../../../context/AuthContext';
import TimesheetGrid from '../Employ/TimesheetGrid';

/**
 * Timesheet component.
 * @example
 * return (
 *   <Timesheet />
 * )
 * @returns {React.ReactElement} component.
 */
const Timesheet = () => {
  // Set context hooks.
  const { user } = useContext(AuthContext);

  // Set state hooks.
  const [defaultTimesheetDate, setDefaultTimesheetDate] = useState('');
  const [defaultStartTime, setDefaultStartTime] = useState('');
  const [defaultEndTime, setDefaultEndTime] = useState('');
  const [isHeadcountDisabled, setIsHeadcountDisabled] = useState(false);
  const [defaultPayRate, setDefaultPayRate] = useState(null);
  const [currentUser, setCurrentUser] = useState(user);
  const [pendingFormData, setPendingFormData] = useState(null);

  // Set effect hook.
  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  // Set form hooks.
  const { control, handleSubmit, setValue, getValues } = useForm();

  // Set effect hook.
  useEffect(() => {
    const today = getTodaysDate();
    const roundedStartTime = roundDownTimeToHalfHour(new Date());
    const roundedEndTime = addHours(roundedStartTime, 1);

    setDefaultTimesheetDate(today);
    setDefaultStartTime(roundedStartTime);
    setDefaultEndTime(roundedEndTime);

    setValue('date', today); // Set the default value for the date field
    setValue('start_time', roundedStartTime); // Set the default value for the start time field
    setValue('end_time', roundedEndTime); // Set the default value for the end time field
    setValue('headcount', isHeadcountDisabled ? 0 : 1); // Set the default value for the headcount field
  }, [setValue]);

  // Set query hooks.
  const {
    isLoading: getRatesIsLoading,
    isError: getRatesError,
    data: payRates = [],
    isSuccess: getRatesSucess,
    refetch: refetchRates,
  } = useGetUserPayRatesQuery(currentUser?.id, {
    skip: !currentUser?.id,
  });

  const {
    isLoading: getActivitiesIsLoading,
    isError: getActivitiesError,
    data: activities = [],
    refetch: refetchActivities,
  } = useGetUserActivitiesQuery(currentUser?.id, {
    skip: !currentUser?.id,
  });
  const { isLoading: getUsersIsLoading, isError: getUsersError, data: users = [] } = useGetUsersQuery();

  // Refetch activities when the selected user changes
  useEffect(() => {
    if (currentUser?.id) {
      refetchActivities();
      refetchRates();
    }
  }, [currentUser, refetchActivities]);

  // Set mutation hooks.
  const [addTimesheet, { isLoading: isAdding }] = useAddActivityMutation();

  // Set watch hooks.
  const startTimeValue = useWatch({ control, name: 'start_time' });
  const endTimeValue = useWatch({ control, name: 'end_time' });
  const payRateValue = useWatch({ control, name: 'pay_rate' });

  // Set effect hook to handle pay_rate changes.
  useEffect(() => {
    if (payRateValue && payRates.length > 0) {
      const selectedPayRate = payRates.find((rate) => rate.id === payRateValue);
      if (selectedPayRate) {
        setValue('headcount', selectedPayRate.flat_rate ? 0 : 1);
        setIsHeadcountDisabled(!!selectedPayRate.flat_rate);
        setDefaultPayRate(payRateValue);
      } else {
        // Set default pay rate if selected is not in the list
        const defaultRate = payRates[0].id;
        setValue('pay_rate', defaultRate);
        setDefaultPayRate(defaultRate);
        setIsHeadcountDisabled(payRates[0].flat_rate);
      }
    } else if (payRates.length > 0) {
      const defaultRate = payRates[0].id;
      setValue('pay_rate', defaultRate);
      setDefaultPayRate(defaultRate);
      setIsHeadcountDisabled(payRates[0].flat_rate);
    }
  }, [payRateValue, payRates, setValue]);

  // Set effect hook.
  useEffect(() => {
    if (startTimeValue && endTimeValue) {
      const start = new Date(`1970-01-01T${startTimeValue}`);
      const end = new Date(`1970-01-01T${endTimeValue}`);

      if (end <= start) {
        setValue('end_time', addHours(startTimeValue, 1));
      }
    }
  }, [startTimeValue, endTimeValue, setValue]);

  // Function to update the form with row data, except for the date
  const handleReplay = (rowData) => {
    setValue('activity', rowData.activity);
    setValue('start_time', rowData.start_time);
    setValue('end_time', rowData.end_time);
    setValue('headcount', rowData.headcount);
    setValue('pay_rate', rowData.pay_rate);
  };
  // Check for error state.
  if (getUsersError) return <ErrorAlert error={getRatesError} fallback="Error loading employees." />;
  if (getRatesError) return <ErrorAlert error={getRatesError} fallback="Error loading pay rates." />;
  if (getRatesSucess && payRates.length === 0)
    return <ErrorAlert error="No pay rates found. Please notify management." severity="warning" />;

  /**
   * Persist a timesheet entry.
   * @param {Object} data - Form data.
   * @returns {void}
   */
  const submitFormData = async (data) => {
    const formData = {
      ...data,
      user: currentUser.id, // Assuming user object has an `id` property
      headcount: data.headcount === '' ? 0 : data.headcount, // Convert empty headcount to zero
      pay_rate: data.pay_rate === '' ? null : data.pay_rate, // Convert empty pay_rate to null
    };
    await addTimesheet(formData);
    refetchActivities();
  };

  /**
   * Handle form submission. Prompts to confirm long durations (catches AM/PM mistakes).
   * @param {Object} data - Form data.
   * @returns {void}
   */
  const onSubmit = async (data) => {
    if (isLongDuration(data.start_time, data.end_time)) {
      setPendingFormData(data);
      return;
    }
    await submitFormData(data);
  };

  // Handle confirm/cancel of the long-duration dialog.
  const handleConfirmLongDuration = async () => {
    const data = pendingFormData;
    setPendingFormData(null);
    if (data) await submitFormData(data);
  };
  const handleCancelLongDuration = () => setPendingFormData(null);

  /**
   * Handle employee change.
   * @param {Object} event - Event object.
   * @returns {void}
   */
  const handleEmployeeChange = (event) => {
    const selectedUserId = event.target.value;
    const selectedUser = users.find((usr) => usr.id === selectedUserId);
    setCurrentUser(selectedUser);
    refetchRates();
    refetchActivities();
  };

  // Get distinct activities ignoring case but keeping the case of at least one occurrence.
  const activityMap = new Map();
  activities.forEach((item) => {
    const lowerCaseActivity = item.activity.toLowerCase();
    if (!activityMap.has(lowerCaseActivity)) {
      activityMap.set(lowerCaseActivity, item.activity);
    }
  });
  const activitySuggestions = [...activityMap.values()];

  // Render component.
  return (
    <AuthMenuGroup>
      {isAdding && <PageSpinner />}
      <Box>
        <Typography variant="h1">Edit Timesheets</Typography>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ width: '100%' }}>
        <Grid container spacing={1}>
          <Grid xs={3}>
            {getUsersIsLoading ? (
              <PageSpinner backdrop={false} />
            ) : (
              <FormControl fullWidth margin="dense">
                <InputLabel id="employee-label">Employee</InputLabel>
                <Select
                  labelId="employee-label"
                  id="employee"
                  label="Employee"
                  value={currentUser ? currentUser.id : ''}
                  onChange={handleEmployeeChange}
                >
                  {[...users]
                    .sort((a, b) => a.first_name.localeCompare(b.first_name))
                    .map((employee) => {
                      return (
                        <MenuItem key={employee.id} value={employee.id}>
                          {employee.first_name} {employee.last_name}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid xs={9} />
          <Grid xs={6} md={3.5}>
            <Controller
              name="activity"
              control={control}
              defaultValue={null}
              rules={{ required: 'Activity is required.' }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  {...field}
                  disablePortal
                  options={activitySuggestions}
                  getOptionLabel={(option) => option || ''}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Activity"
                      margin="dense"
                      error={!!fieldState.error}
                      helperText={fieldState.error ? fieldState.error.message : null}
                    />
                  )}
                  onChange={(event, value) => field.onChange(value)}
                  onInputChange={(event, value) => {
                    if (value) {
                      field.onChange(value);
                    } else {
                      field.onChange(null);
                    }
                  }}
                  value={field.value || null}
                />
              )}
            />
          </Grid>
          <Grid xs={6} md={1.5}>
            <FormText
              control={control}
              name="date"
              label="Date"
              type="date"
              defaultValue={defaultTimesheetDate}
              rules={{ required: 'Date is required.' }}
              margin="dense"
              shrinkLabel
            />
          </Grid>
          <Grid xs={6} md={1.5}>
            <FormText
              control={control}
              name="start_time"
              label="Start Time"
              type="time"
              defaultValue={defaultStartTime}
              rules={{ required: 'Start time is required.' }}
              margin="dense"
              shrinkLabel
            />
          </Grid>
          <Grid xs={6} md={1.5}>
            <FormText
              control={control}
              name="end_time"
              label="End Time"
              type="time"
              defaultValue={defaultEndTime}
              rules={{
                required: 'End time is required.',
                validate: (value) => {
                  const startValue = startTimeValue || getValues('start_time');
                  const start = new Date(`1970-01-01T${startValue}`);
                  const end = new Date(`1970-01-01T${value}`);
                  return end > start || 'End time must be later than start time.';
                },
              }}
              margin="dense"
              shrinkLabel
            />
          </Grid>
          <Grid xs={6} md={1.5}>
            {getRatesIsLoading ? (
              <PageSpinner backdrop={false} />
            ) : (
              <FormControl fullWidth margin="dense">
                <Controller
                  name="pay_rate"
                  control={control}
                  defaultValue={defaultPayRate || payRates[0].id}
                  render={({ field, fieldState }) => (
                    <Select {...field} labelId="rate-label" error={!!fieldState.error} label="Pay Rate">
                      {payRates.map((rate) => (
                        <MenuItem key={rate.id} value={rate.id}>
                          {`${rate.rate_name}: $${rate.rate}/${rate.flat_rate ? 'hour' : 'head'}`}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <InputLabel id="rate-label" shrink>
                  Pay Rate
                </InputLabel>
              </FormControl>
            )}
          </Grid>
          <Grid xs={6} md={1.5}>
            <FormControl fullWidth margin="dense">
              <Controller
                name="headcount"
                control={control}
                defaultValue={isHeadcountDisabled ? 0 : 1}
                rules={{
                  required: 'Headcount is required.',
                  validate: (value) => isHeadcountDisabled || value > 0 || 'Headcount is required.',
                  min: { value: 0, message: 'Headcount cannot be less than 0.' },
                  max: { value: 99, message: 'Headcount cannot be more than 99.' },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Headcount"
                    type="number"
                    inputProps={{ min: isHeadcountDisabled ? 0 : 1, max: 99 }}
                    error={!!fieldState.error}
                    helperText={fieldState.error ? fieldState.error.message : null}
                    disabled={isHeadcountDisabled}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid xs={12} md={1}>
            <Button type="submit" fullWidth variant="contained" sx={{ marginTop: 2 }}>
              Add
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Box mt={2}>
        {getActivitiesIsLoading && getRatesIsLoading && <PageSpinner backdrop={false} />}
        {getActivitiesError && <ErrorAlert error={getActivitiesError} fallback="Error loading timesheet activities" />}
        {activities && payRates.length > 0 && (
          <Box>
            <TimesheetGrid activities={activities} payRates={payRates} onReplay={handleReplay} />
          </Box>
        )}
      </Box>
      <Question
        title="Double-check this entry"
        question={
          pendingFormData
            ? `This entry is ${calculateHours(pendingFormData.start_time, pendingFormData.end_time)} hours long. Most classes are 1–2 hours — did you set AM/PM correctly?`
            : ''
        }
        option1="Yes, save"
        option2="Let me fix it"
        option1Handler={handleConfirmLongDuration}
        option2Handler={handleCancelLongDuration}
        open={!!pendingFormData}
        onClose={handleCancelLongDuration}
      />
    </AuthMenuGroup>
  );
};

// Export component.
export default Timesheet;
